import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
  ModalOverlay,
  TextInput,
} from '@dev-spendesk/grapes';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';

import { useTranslation } from 'common/hooks/useTranslation';
import type { RecoveryShareMethod } from 'modules/members/components/ConfirmRequesterIdentityModal/ConfirmRequesterIdentityModal';
import { logger } from 'src/utils/datadog-log-wrapper';

type Props =
  | {
      memberEmail?: never;
      onCancel: () => void;
      recoveryShareMethod: Extract<RecoveryShareMethod, 'manual'>;
      recoveryCode: string;
    }
  | {
      memberEmail: string;
      onCancel: () => void;
      recoveryShareMethod: Extract<RecoveryShareMethod, 'email'>;
      recoveryCode?: never;
    };

export const RecoveryFactorModal = ({
  memberEmail,
  onCancel,
  recoveryCode,
  recoveryShareMethod,
}: Props) => {
  return (
    <ModalOverlay isOpen>
      {recoveryShareMethod === 'manual' && (
        <RecoveryCodeModal
          onCancel={onCancel}
          onConfirm={onCancel}
          recoveryCode={recoveryCode}
        />
      )}
      {recoveryShareMethod === 'email' && (
        <NotificationSuccessModal
          email={memberEmail}
          onCancel={onCancel}
          onConfirm={onCancel}
        />
      )}
    </ModalOverlay>
  );
};

const RecoveryCodeModal = ({
  onCancel,
  onConfirm,
  recoveryCode,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  recoveryCode: string;
}) => {
  const { t } = useTranslation('global');
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyRecoveryCode = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setIsCopied(true);
    } catch {
      // ignore error thrown
      logger.info('Failed to copy recovery code to clipboard', {
        scope: 'user-authentication-factors',
        team: 'cards-ux',
      });
    }
  };

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        iconName="lock-close"
        iconVariant="neutral"
        title={t(
          'members.editModal.security.recoveryFactorModal.recoveryDisplayModal.title',
        )}
      />
      <ModalBody>
        <div className="flex w-full flex-col items-center justify-center px-40 py-32">
          <div className="flex gap-8">
            {recoveryCode.split('').map((digit) => (
              // eslint-disable-next-line react/jsx-key
              <TextInput
                isReadOnly
                className="w-32"
                value={digit}
                maxLength={1}
                isDisabled
                textAlign="center"
                fit="content"
                data-mask
              />
            ))}
          </div>
          <Button
            className="mt-8"
            variant="tertiaryBrand"
            text={isCopied ? t('misc.copied') : t('misc.copy')}
            onClick={handleCopyRecoveryCode}
          />
        </div>
        <p className="text-content-secondary-bg-primary body-m">
          <Trans i18nKey="members.editModal.security.recoveryFactorModal.recoveryDisplayModal.description" />
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onCancel}
        />
        <Button
          key="confirm"
          variant="primaryBrand"
          text={t('misc.done')}
          onClick={onConfirm}
        />
      </ModalFooter>
    </ModalContent>
  );
};

const NotificationSuccessModal = ({
  email,
  onCancel,
  onConfirm,
}: {
  email: string;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation('global');

  return (
    <ModalContent>
      <ModalHeaderWithIcon
        iconName="circle-check"
        iconVariant="success"
        title={t(
          'members.editModal.security.recoveryFactorModal.recoveryCodeEmailSentModal.title',
        )}
      />
      <ModalBody>
        <Trans
          className="text-content-secondary-bg-primary body-m"
          i18nKey="members.editModal.security.recoveryFactorModal.recoveryCodeEmailSentModal.description"
          components={{
            bold: <span className="title-m" />,
          }}
          values={{
            email,
          }}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          key="cancel"
          variant="secondaryNeutral"
          text={t('misc.cancel')}
          onClick={onCancel}
        />
        <Button
          key="confirm"
          variant="primaryBrand"
          text={t('misc.done')}
          onClick={onConfirm}
        />
      </ModalFooter>
    </ModalContent>
  );
};
